import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Tag, Settings, DollarSign, Calendar, FileText, UserCheck, Shield, CheckCircle } from "react-feather";
import { formatDateYYYYMMDD } from "../../helpers/dateUtils";

const AddEdit_CouponMaster = () => {
  const API_URL_SAVE = "CouponMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/CouponMaster/Id";
  
  // Type dropdown options
  const typeOptions = [
    { Id: 1, Name: 'Flat Discount (₹)' },
    { Id: 2, Name: 'Percentage (%)' }
  ];

  // IsAvailableForAll dropdown options
  const yesNoOptions = [
    { Id: 1, Name: 'Yes' },
    { Id: 2, Name: 'No' }
  ];

  // Active status options
  const activeOptions = [
    { Id: 1, Name: 'Active' },
    { Id: 2, Name: 'Inactive' }
  ];

  const [state, setState] = useState({
    id: 0,
    formData: {
      Name: "",
      CouponCount: "",
      Type: "",
      Value: "",
      MaxDiscountAmount: "",
      MinOrderAmount: "",
      ValidTillDate: "",
      Remarks: "",
      IsAvailableForAll: "1",
      UsageLimitPerUser: "1",
      IsFirstOrderOnly: "2",
      IsActive: "1",
    },
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;
    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    Name: Yup.string()
      .required("Coupon code / name is required")
      .min(2, "Coupon code must be at least 2 characters")
      .max(500, "Coupon code must be less than 500 characters"),
    CouponCount: Yup.number()
      .typeError("Total coupon count must be a valid number")
      .required("Total coupon count is required")
      .min(1, "Coupon count must be at least 1")
      .integer("Coupon count must be a whole number"),
    UsageLimitPerUser: Yup.number()
      .typeError("Per-user limit must be a valid number")
      .required("Per-user limit is required (e.g. 1 for single-use)")
      .min(0, "Per-user limit cannot be negative")
      .integer("Must be a whole number"),
    Type: Yup.string()
      .required("Type is required"),
    Value: Yup.number()
      .typeError("Value must be a valid number")
      .required("Value is required")
      .min(0.01, "Value must be greater than 0")
      .when("Type", {
        is: "2", // Percent
        then: (schema) => schema.max(100, "Percentage value cannot exceed 100"),
        otherwise: (schema) => schema,
      }),
    MaxDiscountAmount: Yup.number()
      .typeError("Max discount amount must be a valid number")
      .nullable()
      .min(0, "Max discount amount cannot be negative"),
    MinOrderAmount: Yup.number()
      .typeError("Min order amount must be a valid number")
      .nullable()
      .min(0, "Min order amount cannot be negative"),
    ValidTillDate: Yup.date()
      .nullable()
      .typeError("Please enter a valid date"),
    Remarks: Yup.string()
      .nullable()
      .max(1000, "Remarks must be less than 1000 characters"),
    IsAvailableForAll: Yup.string()
      .required("Is Available For All is required"),
    IsFirstOrderOnly: Yup.string()
      .required("First order only option is required"),
    IsActive: Yup.string()
      .required("Status is required"),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("Name", values.Name ? values.Name.trim().toUpperCase() : "");
    formData.append("CouponCount", values.CouponCount ?? 0);
    formData.append("UsageLimitPerUser", values.UsageLimitPerUser ?? 1);
    formData.append("IsFirstOrderOnly", String(values.IsFirstOrderOnly) === "1" ? "true" : "false");
    formData.append("IsActive", String(values.IsActive) === "1" ? "true" : "false");
    formData.append("Type", values.Type || "");
    formData.append("Value", values.Value ?? 0);
    formData.append("MaxDiscountAmount", values.MaxDiscountAmount ?? "");
    formData.append("MinOrderAmount", values.MinOrderAmount ?? "");
    formData.append("ValidTillDate", values.ValidTillDate || "");
    formData.append("Remarks", values.Remarks || "");
    formData.append("IsAvailableForAll", values.IsAvailableForAll || "1");
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "couponmasterid",
      navigate,
      "/CouponMaster"
    );
  };

  // Helper to map boolean / number to dropdown string
  const getDropdownVal = (val, defaultVal = "1") => {
    if (val === true || val === 1 || val === "1" || val === "true") return "1";
    if (val === false || val === 0 || val === 2 || val === "2" || val === "false") return "2";
    return defaultVal;
  };

  return (
    <div style={{ padding: "6px 8px", maxWidth: 900, margin: "0 auto" }}>
      <Card className="border shadow-none" style={{ marginBottom: 0 }}>
        <CardBody className="p-2" style={{ padding: "8px 12px !important" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid #dee2e6" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
              <Tag size={16} className="me-2 text-primary" />
              {state.id > 0 ? "Edit Coupon" : "Create New Coupon"}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button type="button" size="sm" color="light" className="py-1 px-2" style={{ fontSize: 12 }} onClick={() => navigate("/CouponMaster")}><ArrowLeft size={12} className="me-1" />Back</Button>
              <Button type="submit" form="coupon-form" size="sm" color="primary" className="py-1 px-2" style={{ fontSize: 12 }}><Save size={12} className="me-1" />Save Coupon</Button>
            </div>
          </div>
          <Formik 
            initialValues={{
              Name: state.formData?.Name ?? "",
              CouponCount: state.formData?.CouponCount ?? "",
              UsageLimitPerUser: state.formData?.UsageLimitPerUser ?? "1",
              IsFirstOrderOnly: getDropdownVal(state.formData?.IsFirstOrderOnly, "2"),
              IsActive: getDropdownVal(state.formData?.IsActive, "1"),
              Type: state.formData?.Type ? String(state.formData.Type) : "",
              Value: state.formData?.Value ?? "",
              MaxDiscountAmount: state.formData?.MaxDiscountAmount ?? "",
              MinOrderAmount: state.formData?.MinOrderAmount ?? "",
              ValidTillDate: state.formData?.ValidTillDate ? formatDateYYYYMMDD(state.formData.ValidTillDate) : "",
              Remarks: state.formData?.Remarks ?? "",
              IsAvailableForAll: getDropdownVal(state.formData?.IsAvailableForAll, "1"),
            }} 
            validationSchema={validationSchema} 
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form id="coupon-form">
                <Row className="g-3">
                  {/* Coupon Code / Name */}
                  <Col md={6}>
                    <div className="form-group mb-2">
                      <label htmlFor="Name" className="master-form-label">
                        <Tag size={14} className="me-1" />
                        Coupon Code / Name <span className="required text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Field
                          innerRef={nameRef}
                          className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                          type="text"
                          name="Name"
                          placeholder="e.g. FIRST50, FESTIVE20"
                          style={{ textTransform: "uppercase", fontWeight: 600, letterSpacing: 1 }}
                        />
                      </div>
                      <ErrorMessage name="Name" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        Unique promo code customers enter in the app.
                      </div>
                    </div>
                  </Col>

                  {/* Status (Active / Inactive) */}
                  <Col md={6}>
                    <div className="form-group mb-2">
                      <label htmlFor="IsActive" className="master-form-label">
                        <CheckCircle size={14} className="me-1" />
                        Coupon Status <span className="required text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Field
                          as="select"
                          className={`master-form-input ${errors.IsActive && touched.IsActive ? 'is-invalid' : ''}`}
                          name="IsActive"
                        >
                          {activeOptions.map((opt) => (
                            <option key={opt.Id} value={opt.Id}>
                              {opt.Name}
                            </option>
                          ))}
                        </Field>
                      </div>
                      <ErrorMessage name="IsActive" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        Set to Inactive to pause coupon redemption anytime.
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Rules & Limits Row */}
                <Row className="g-3 mt-1">
                  {/* Usage Limit Per User */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="UsageLimitPerUser" className="master-form-label">
                        <UserCheck size={14} className="me-1" />
                        Usage Limit Per User <span className="required text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Field
                          className={`master-form-input ${errors.UsageLimitPerUser && touched.UsageLimitPerUser ? 'is-invalid' : ''}`}
                          type="number"
                          name="UsageLimitPerUser"
                          placeholder="1"
                          min="0"
                        />
                      </div>
                      <ErrorMessage name="UsageLimitPerUser" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        Max times 1 user can use this (1 = single use, 0 = unlimited).
                      </div>
                    </div>
                  </Col>

                  {/* First Order Only */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="IsFirstOrderOnly" className="master-form-label">
                        <Shield size={14} className="me-1" />
                        First Ride / Booking Only? <span className="required text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Field
                          as="select"
                          className={`master-form-input ${errors.IsFirstOrderOnly && touched.IsFirstOrderOnly ? 'is-invalid' : ''}`}
                          name="IsFirstOrderOnly"
                        >
                          <option value="2">No (Any user)</option>
                          <option value="1">Yes (New Users Only - 1st Ride)</option>
                        </Field>
                      </div>
                      <ErrorMessage name="IsFirstOrderOnly" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        If Yes, users with 1+ completed rides cannot use it.
                      </div>
                    </div>
                  </Col>

                  {/* Total Overall Count / Inventory */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="CouponCount" className="master-form-label">
                        <Tag size={14} className="me-1" />
                        Total Available Count <span className="required text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Field
                          className={`master-form-input ${errors.CouponCount && touched.CouponCount ? 'is-invalid' : ''}`}
                          type="number"
                          name="CouponCount"
                          placeholder="e.g. 500"
                          min="1"
                        />
                      </div>
                      <ErrorMessage name="CouponCount" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        Total number of times coupon can be redeemed across platform.
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Discount & Pricing Row */}
                <Row className="g-3 mt-1">
                  {/* Type */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="Type" className="master-form-label">
                        <Settings size={14} className="me-1" />
                        Discount Type <span className="required text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Field
                          as="select"
                          className={`master-form-input ${errors.Type && touched.Type ? 'is-invalid' : ''}`}
                          name="Type"
                        >
                          <option value="">Select Type</option>
                          {typeOptions.map((option) => (
                            <option key={option.Id} value={option.Id}>
                              {option.Name}
                            </option>
                          ))}
                        </Field>
                      </div>
                      <ErrorMessage name="Type" component="div" className="text-danger small mt-1" />
                    </div>
                  </Col>

                  {/* Discount Value */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="Value" className="master-form-label">
                        <DollarSign size={14} className="me-1" />
                        Discount Value <span className="required text-danger">*</span>
                        {values.Type === "2" && <span className="text-muted ms-1">(Max 100%)</span>}
                      </label>
                      <div className="input-wrapper">
                        <Field
                          className={`master-form-input ${errors.Value && touched.Value ? 'is-invalid' : ''}`}
                          type="number"
                          name="Value"
                          placeholder={values.Type === "2" ? "e.g. 20 for 20%" : "e.g. 50 for ₹50"}
                          min="0"
                          max={values.Type === "2" ? "100" : undefined}
                          step="0.01"
                        />
                      </div>
                      <ErrorMessage name="Value" component="div" className="text-danger small mt-1" />
                    </div>
                  </Col>

                  {/* Max Discount Amount */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="MaxDiscountAmount" className="master-form-label">
                        <DollarSign size={14} className="me-1" />
                        Max Discount Cap (₹)
                      </label>
                      <div className="input-wrapper">
                        <Field
                          className={`master-form-input ${errors.MaxDiscountAmount && touched.MaxDiscountAmount ? 'is-invalid' : ''}`}
                          type="number"
                          name="MaxDiscountAmount"
                          placeholder="e.g. 100 (For % coupons)"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <ErrorMessage name="MaxDiscountAmount" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        Upper limit on discount for percentage coupons.
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Constraints & Validity Row */}
                <Row className="g-3 mt-1">
                  {/* Min Order Amount */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="MinOrderAmount" className="master-form-label">
                        <DollarSign size={14} className="me-1" />
                        Min Booking Fare (₹)
                      </label>
                      <div className="input-wrapper">
                        <Field
                          className={`master-form-input ${errors.MinOrderAmount && touched.MinOrderAmount ? 'is-invalid' : ''}`}
                          type="number"
                          name="MinOrderAmount"
                          placeholder="e.g. 150"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <ErrorMessage name="MinOrderAmount" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        Minimum ride fare required to apply this coupon.
                      </div>
                    </div>
                  </Col>

                  {/* Valid Till Date */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="ValidTillDate" className="master-form-label">
                        <Calendar size={14} className="me-1" />
                        Expiry Date
                      </label>
                      <div className="input-wrapper">
                        <Field
                          className={`master-form-input ${errors.ValidTillDate && touched.ValidTillDate ? 'is-invalid' : ''}`}
                          type="date"
                          name="ValidTillDate"
                        />
                      </div>
                      <ErrorMessage name="ValidTillDate" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        Coupon will expire after this date (optional).
                      </div>
                    </div>
                  </Col>

                  {/* Is Available For All */}
                  <Col md={4}>
                    <div className="form-group mb-2">
                      <label htmlFor="IsAvailableForAll" className="master-form-label">
                        <Settings size={14} className="me-1" />
                        Show in App Offers List? <span className="required text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Field
                          as="select"
                          className={`master-form-input ${errors.IsAvailableForAll && touched.IsAvailableForAll ? 'is-invalid' : ''}`}
                          name="IsAvailableForAll"
                        >
                          {yesNoOptions.map((opt) => (
                            <option key={opt.Id} value={opt.Id}>
                              {opt.Name}
                            </option>
                          ))}
                        </Field>
                      </div>
                      <ErrorMessage name="IsAvailableForAll" component="div" className="text-danger small mt-1" />
                      <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        If Yes, visible in 'Available Coupons' list in Customer App.
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Remarks / Description */}
                <div className="form-group mb-2 mt-2">
                  <label htmlFor="Remarks" className="master-form-label">
                    <FileText size={14} className="me-1" />
                    Offer Description / Remarks
                  </label>
                  <div className="input-wrapper">
                    <Field
                      as="textarea"
                      className={`master-form-input ${errors.Remarks && touched.Remarks ? 'is-invalid' : ''}`}
                      name="Remarks"
                      placeholder="e.g. Get 20% discount on your first booking up to ₹100!"
                      rows="2"
                    />
                  </div>
                  <ErrorMessage name="Remarks" component="div" className="text-danger small mt-1" />
                  <div className="form-help-text text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                    Customer-facing offer description displayed in the App.
                  </div>
                </div>

              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddEdit_CouponMaster;
